import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Wallet, Plus, Copy, QrCode, Clock, CheckCircle, XCircle, 
  AlertCircle, Loader2, ExternalLink, Trash2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Crypto icons mapping
const getCryptoIcon = (code) => {
  const icons = {
    'BTC': '₿',
    'ETH': 'Ξ',
    'USDT_ERC20': '₮',
    'USDT_TRC20': '₮',
    'USDC': '$',
    'SOL': '◎',
    'BNB': 'BNB',
    'XRP': 'XRP',
    'ADA': '₳',
    'DOGE': 'Ð',
    'DOT': '●',
    'MATIC': 'MATIC',
    'LTC': 'Ł',
    'AVAX': 'AVAX',
    'LINK': 'LINK',
    'TRX': 'TRX'
  };
  return icons[code] || code;
};

// Crypto colors
const getCryptoColor = (code) => {
  const colors = {
    'BTC': 'bg-orange-500/20 text-orange-500 border-orange-500/50',
    'ETH': 'bg-purple-500/20 text-purple-500 border-purple-500/50',
    'USDT_ERC20': 'bg-green-500/20 text-green-500 border-green-500/50',
    'USDT_TRC20': 'bg-green-500/20 text-green-500 border-green-500/50',
    'USDC': 'bg-blue-500/20 text-blue-500 border-blue-500/50',
    'SOL': 'bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-400 border-purple-500/50',
    'BNB': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
    'XRP': 'bg-gray-500/20 text-gray-400 border-gray-500/50',
    'ADA': 'bg-blue-600/20 text-blue-400 border-blue-500/50',
    'DOGE': 'bg-amber-500/20 text-amber-500 border-amber-500/50',
    'DOT': 'bg-pink-500/20 text-pink-500 border-pink-500/50',
    'MATIC': 'bg-purple-600/20 text-purple-400 border-purple-500/50',
    'LTC': 'bg-gray-400/20 text-gray-300 border-gray-400/50',
    'AVAX': 'bg-red-500/20 text-red-500 border-red-500/50',
    'LINK': 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    'TRX': 'bg-red-600/20 text-red-400 border-red-500/50'
  };
  return colors[code] || 'bg-gray-500/20 text-gray-400 border-gray-500/50';
};

export default function ClientWallets() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const [wallets, setWallets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';
  
  // Check if in mirror mode and get the real client ID
  const [effectiveClientId, setEffectiveClientId] = useState(null);
  
  useEffect(() => {
    const mirrorMode = localStorage.getItem('mirrorMode') === 'true';
    const mirrorClientId = localStorage.getItem('mirrorClientId');
    const clientId = mirrorMode && mirrorClientId ? mirrorClientId : user?.id;
    setEffectiveClientId(clientId);
  }, [user?.id]);

  useEffect(() => {
    if (effectiveClientId) {
      fetchData();
    }
  }, [effectiveClientId]);

  const fetchData = async () => {
    if (!effectiveClientId) return;
    try {
      const [walletsRes, requestsRes, currenciesRes] = await Promise.all([
        axios.get(`${API}/crypto/wallets/${effectiveClientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/crypto/requests/${effectiveClientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/crypto/currencies`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setWallets(walletsRes.data || []);
      setRequests(requestsRes.data || []);
      setCurrencies(currenciesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestWallet = async () => {
    if (!selectedCurrency) {
      toast.error('Veuillez sélectionner une monnaie');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/crypto/requests`, {
        currency_code: selectedCurrency,
        client_id: effectiveClientId  // Send client_id for mirror mode support
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Demande de wallet envoyée');
      setShowRequestDialog(false);
      setSelectedCurrency('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/crypto/requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande annulée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Adresse copiée !');
  };

  const openQrDialog = (wallet) => {
    setSelectedWallet(wallet);
    setShowQrDialog(true);
  };

  // Get available currencies (not already owned or requested)
  const availableCurrencies = currencies.filter(c => 
    !wallets.some(w => w.currency_code === c.code) &&
    !requests.some(r => r.currency_code === c.code && r.status === 'pending')
  );

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Mes Wallets Crypto</h1>
            <p className="text-muted-foreground mt-1">
              Gérez vos adresses de dépôt crypto
            </p>
          </div>
          <Button 
            onClick={() => setShowRequestDialog(true)}
            style={{ backgroundColor: primaryColor }}
            disabled={availableCurrencies.length === 0}
            data-testid="request-wallet-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Demander un wallet
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Wallet className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Wallets actifs</p>
                  <p className="text-2xl font-bold text-white">{wallets.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/20">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Demandes en attente</p>
                  <p className="text-2xl font-bold text-white">{pendingRequests.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cryptos disponibles</p>
                  <p className="text-2xl font-bold text-white">{currencies.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Wallets */}
        <Card className="bg-card/50 backdrop-blur border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Wallet className="w-5 h-5" />
              Mes Wallets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {wallets.length === 0 ? (
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun wallet actif</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Faites une demande pour obtenir votre premier wallet
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wallets.map((wallet) => (
                  <div 
                    key={wallet.id}
                    className="p-4 rounded-lg border border-border/50 bg-background/50 hover:border-primary/50 transition-colors"
                    data-testid={`wallet-${wallet.currency_code}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${getCryptoColor(wallet.currency_code)}`}>
                          {getCryptoIcon(wallet.currency_code)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{wallet.currency_name}</h3>
                          <p className="text-xs text-muted-foreground">{wallet.network}</p>
                        </div>
                      </div>
                      {wallet.qr_code_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openQrDialog(wallet)}
                          className="text-muted-foreground hover:text-white"
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="bg-background/80 rounded p-2 mb-3">
                      <p className="text-xs text-muted-foreground mb-1">Adresse de dépôt</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-white font-mono break-all flex-1">
                          {wallet.wallet_address}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyAddress(wallet.wallet_address)}
                          className="shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Créé le {new Date(wallet.created_at).toLocaleDateString('fr-FR')}</span>
                      <Badge variant="outline" className="bg-green-500/20 text-green-500 border-green-500/50">
                        Actif
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Clock className="w-5 h-5 text-yellow-500" />
                Demandes en attente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingRequests.map((request) => (
                  <div 
                    key={request.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${getCryptoColor(request.currency_code)}`}>
                        {getCryptoIcon(request.currency_code)}
                      </div>
                      <div>
                        <p className="font-medium text-white">{request.currency_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Demandé le {new Date(request.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50">
                        En attente
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelRequest(request.id)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request History */}
        {processedRequests.length > 0 && (
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <AlertCircle className="w-5 h-5" />
                Historique des demandes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {processedRequests.slice(0, 5).map((request) => (
                  <div 
                    key={request.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-background/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${getCryptoColor(request.currency_code)}`}>
                        {getCryptoIcon(request.currency_code)}
                      </div>
                      <div>
                        <p className="font-medium text-white">{request.currency_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(request.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={request.status === 'approved' 
                        ? 'bg-green-500/20 text-green-500 border-green-500/50'
                        : 'bg-red-500/20 text-red-500 border-red-500/50'
                      }
                    >
                      {request.status === 'approved' ? 'Approuvée' : 'Refusée'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request Wallet Dialog */}
        <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-white">Demander un nouveau wallet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-white mb-2 block">
                  Choisissez une crypto-monnaie
                </label>
                <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                  <SelectTrigger className="bg-background border-border" data-testid="currency-select">
                    <SelectValue placeholder="Sélectionner une crypto" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-[300px]">
                    {availableCurrencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getCryptoColor(currency.code)}`}>
                            {getCryptoIcon(currency.code)}
                          </span>
                          <span>{currency.name}</span>
                          <span className="text-xs text-muted-foreground">({currency.network})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedCurrency && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-sm text-blue-400">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Votre demande sera examinée par notre équipe. Vous recevrez une notification une fois le wallet créé.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleRequestWallet}
                disabled={!selectedCurrency || submitting}
                style={{ backgroundColor: primaryColor }}
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Envoyer la demande
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                {selectedWallet?.currency_name} - QR Code
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-4 space-y-4">
              {selectedWallet?.qr_code_url && (
                <div className="bg-white p-4 rounded-lg">
                  <img 
                    src={selectedWallet.qr_code_url} 
                    alt="QR Code"
                    className="w-48 h-48 object-contain"
                  />
                </div>
              )}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Adresse de dépôt</p>
                <code className="text-xs text-white font-mono break-all bg-background/80 p-2 rounded block">
                  {selectedWallet?.wallet_address}
                </code>
              </div>
              <Button
                variant="outline"
                onClick={() => copyAddress(selectedWallet?.wallet_address)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier l'adresse
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
